import {SharedColors} from "@fluentui/theme";
import {
    DatePicker,
    DayOfWeek,
    IButtonProps,
    Icon,
    IconButton,
    IDatePicker,
    Label,
    Stack,
    VerticalDivider,
} from "@fluentui/react";
import React, {FC, useCallback, useContext, useMemo, useRef,} from "react";
import {useTranslation} from "react-i18next";
import {calendarStrings} from "../helpers/CaledarStrings";
import {hr} from "../locales/hr";
import {LanguageContext} from "../contexts/LanguageContext";
import {useErrorMessage} from "../hooks/useErrorMessage";

export interface ILocalDatePickerProps {
    value?: Date;
    onSelectDate?: (d?: Date | null) => void;
    label?: keyof typeof hr | string;
    disableAutoFocus?: boolean;
    keepTime?: boolean;
    styles?: any;
    placeholder?: keyof typeof hr | string;
    disabled?: boolean;
    showCancel?: boolean;
    title?: string;
    tabIndex?: number;
    onKeyDownCapture?: (event: React.KeyboardEvent<HTMLElement>) => void;
    errorMessage?: string;
    rightSideIconButtonProps?: IButtonProps;
    minDate?: Date;
    maxDate?: Date;
    horizontal?: boolean;
    id?: string;
    short?: boolean;
    utcOnly?: boolean;
    keepCompact?: boolean;
    className?: string;
}

export const LocalDatePicker: FC<ILocalDatePickerProps> = ({
                                                               keepCompact,
                                                               className,
                                                               utcOnly,
                                                               value,
                                                               title,
                                                               short,
                                                               id,
                                                               minDate,
                                                               horizontal,
                                                               maxDate,
                                                               onKeyDownCapture,
                                                               errorMessage,
                                                               showCancel,
                                                               tabIndex,
                                                               disabled,
                                                               styles,
                                                               placeholder,
                                                               onSelectDate,
                                                               keepTime,
                                                               label,
                                                               disableAutoFocus,
                                                               rightSideIconButtonProps,
                                                           }) => {
    const {t} = useTranslation();
    const {serverDateToLocalDate, moment} = useContext(LanguageContext);
    const dpRef = useRef<IDatePicker>(null);
    const compRef = useRef<HTMLDivElement>(null);
    const {localErrorMessage, clearLocalErrorMessage} =
        useErrorMessage(errorMessage);

    const dateChanged = useCallback(
        (d?: Date | null) => {
            console.log("dateChanged", d);
            if (d) {
                const dt = utcOnly ? moment(d) : moment(d).local();
                const utcTime = serverDateToLocalDate(value);
                if (keepTime) {
                    if (utcOnly) {
                        const v = moment.utc(value);
                        dt.set({
                            hours: v?.hours(),
                            minutes: v?.minutes(),
                        });
                    } else {
                        dt.set({
                            hours: utcTime?.hours(),
                            minutes: utcTime?.minutes(),
                        });
                    }
                }

                const selectDateToSet = (
                    utcOnly ? moment(dt).add(dt.utcOffset(), "minutes").utc() : dt
                ).toDate();
                onSelectDate?.(selectDateToSet);
            } else {
                onSelectDate?.(undefined);
            }
        },
        [value, onSelectDate, keepTime, utcOnly],
    );

    const showDatePicker = useCallback(() => {
        if (dpRef?.current) {
            dpRef.current.showDatePickerPopup();
        }
        clearLocalErrorMessage();
    }, [dpRef, serverDateToLocalDate, utcOnly, clearLocalErrorMessage]);

    const format = useMemo(() => (short ? "L" : "LL"), [short]);

    return (
        <Stack styles={styles} className={className}>
            {!horizontal && label ? <Label>{t(label as string)}</Label> : null}
            <Stack
                horizontal
                horizontalAlign={"stretch"}
                tokens={{childrenGap: 10}}
                verticalAlign="end"
            >
                {horizontal && label ? (
                    <>
                        <VerticalDivider/>
                        <Label>{t(label as string)}</Label>
                    </>
                ) : null}
                <DatePicker
                    componentRef={dpRef}
                    disabled={disabled}
                    // label={!horizontal && label ? t(label as string) : undefined}
                    disableAutoFocus={disableAutoFocus}
                    key={t(label as string) as any}
                    title={title}
                    id={id}
                    minDate={minDate}
                    ref={compRef}
                    maxDate={maxDate}
                    onClick={showDatePicker}
                    formatDate={(d?: Date) =>
                        (utcOnly ? moment.utc(d) : serverDateToLocalDate(d)).format(format)
                    }
                    onKeyDownCapture={onKeyDownCapture}
                    tabIndex={tabIndex}
                    placeholder={
                        label
                            ? t(label as string)
                            : placeholder
                                ? t(placeholder as string)
                                : undefined
                    }
                    strings={calendarStrings(t, moment)}
                    styles={{
                        root: {flex: 1},
                    }}
                    firstDayOfWeek={DayOfWeek.Monday}
                    value={
                        value
                            ? (utcOnly
                                    ? moment.utc(value)
                                    : serverDateToLocalDate(value)
                            ).toDate()
                            : undefined
                    }
                    textField={{
                        placeholder: t(label as string),
                        value: value
                            ? (utcOnly
                                    ? moment.utc(value)
                                    : serverDateToLocalDate(value)
                            ).format(format)
                            : "",
                        style: disabled ? {color: "#333"} : {},
                        errorMessage: localErrorMessage,
                        iconProps: {iconName: undefined},
                        onRenderSuffix: () =>
                            showCancel && value ? (
                                <Icon
                                    style={{
                                        color: SharedColors.red10,
                                        cursor: "pointer",
                                        padding: 10,
                                    }}
                                    iconName="Cancel"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onSelectDate?.(null);
                                    }}
                                />
                            ) : (
                                <Icon iconName={"Calendar"}/>
                            ),
                        styles: {
                            suffix: {
                                padding: showCancel && value ? 0 : undefined,
                            },
                        },
                    }}
                    onSelectDate={dateChanged}
                />
                {rightSideIconButtonProps ? (
                    <IconButton {...rightSideIconButtonProps} />
                ) : null}
            </Stack>
        </Stack>
    );
};
